import { action, computed, observable } from 'mobx';

import Auth from './AuthManager';
import classroomManager from './ClassroomManager';

export class AccommodationsManager {
  @observable includeAssignmentAccommodations = false;
  @observable includeClassroomAccommodations = false;

  @observable currentAccommodationsRosterMap = new Map();

  @observable togglerSectionOffAccommodationsSet = new Set()

  @observable wordToolsTTSDependent = false;

  /** Satellite flag: show the accommodations Translation Language selector when also publisher aiEnabled. */
  @observable hasAccommodationLocaleSelector = false;

  @observable translationLanguagesCache = null;

  @action clearAll = () => {
    this.clearAccommodationsRoster();
    this.clearTogglerSectionOffAccommodationsSet();
    this.clearTranslationLanguagesCache();
    this.clearLocalesCache();
    this.clearPublisherOptionsCache();
  }

  @action setIncludeAssignmentAccommodations = (includeAssignmentAccommodations) => {
    this.includeAssignmentAccommodations = includeAssignmentAccommodations;
  }

  @action setIncludeClassroomAccommodations = (includeClassroomAccommodations) => {
    this.includeClassroomAccommodations = includeClassroomAccommodations;
  }

  @action addToCurrentAccommodationsRoster = async (roster = []) => {
    for (const rosterItem of roster) {
      const offAccommodations = (rosterItem.accommodationIds || '').split(',')
        .filter((accommodation) => accommodation);
      this.currentAccommodationsRosterMap.set(rosterItem.id, {
        ...rosterItem, offAccommodations
      });
    }
  }

  @action clearTogglerSectionOffAccommodationsSet = () => {
    this.togglerSectionOffAccommodationsSet = new Set();
  }

  @action addToTogglerSectionOffAccommodationsSet = (accommodationId) => {
    this.togglerSectionOffAccommodationsSet.add(accommodationId);
  }

  @action removeFromTogglerSectionOffAccommodationsSet = (accommodationId) => {
    this.togglerSectionOffAccommodationsSet.delete(accommodationId);
  }

  @action fetchAccommodationsRoster = async (classroomId, _page = 0) => {
    try {
      classroomId = classroomId || classroomManager.currentClassroomId;
      this.clearAccommodationsRoster();
      const response = await Auth.fetch(`${Auth.ecms}/api/viewAccommodationsByClassroom?classroomId=${classroomId}`, { method: 'GET' });
      if (response.status === 'SUCCESS') {
        const roster = response.data.sort((a, b) => {
          const lastNameA = a.lastName ? a.lastName : '';
          const lastNameB = b.lastName ? b.lastName : '';

          const firstNameA = a.firstName ? a.firstName : '';
          const firstNameB = b.firstName ? b.firstName : '';

          const predicate1 = lastNameA.localeCompare(lastNameB, 'en', {
            numeric: true
          });
          const predicate2 = firstNameA.localeCompare(firstNameB, 'en', {
            numeric: true
          });
          return predicate1 || predicate2;
        });
        await this.addToCurrentAccommodationsRoster(roster);
      }
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  @action fetchPublisherTranslations = async (sortField = 'name', sortDirection = 'asc', skip = 0, pageSize = 30) => {
    try {
      const params = new URLSearchParams({
        'sort[0][field]': sortField,
        'sort[0][dir]': sortDirection,
        skip: skip.toString(),
        pageSize: pageSize.toString()
      });
      const response = await Auth.fetch(`${Auth.ecms}/api/getPublisherTranslations?${params.toString()}`, { method: 'GET' });
      if (response.status === 'SUCCESS') {
        return {
          data: response.data,
          pageTotal: parseInt(response.pageTotal, 10)
        };
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Fetch and cache publisher translations as { localeId: displayName } format
   * Only fetches if cache is empty
   * @returns {Promise<Object>} - Object with localeId as keys and displayName as values
   */
  @action fetchTranslationLanguages = async () => {
    // Return cached value if available
    if (this.translationLanguagesCache) {
      return this.translationLanguagesCache;
    }

    try {
      const result = await this.fetchPublisherTranslations('displayName', 'asc', 0, 100);
      if (result && result.data) {
        // Transform array to object: { localeId: displayName }
        const translationLanguages = {};
        result.data.forEach((translation) => {
          if (translation.localeId && translation.displayName) {
            translationLanguages[translation.localeId] = translation.displayName;
          }
        });
        // Cache the result
        this.translationLanguagesCache = translationLanguages;
        return translationLanguages;
      }
      return null;
    } catch (error) {
      console.error('Error fetching translation languages:', error);
      return null;
    }
  }

  @action clearTranslationLanguagesCache = () => {
    this.translationLanguagesCache = null;
  }

  // Locales methods
  @observable localesCache = null;

  @action fetchLocales = async (sortField = 'name', sortDirection = 'asc', skip = 0, pageSize = 100) => {
    try {
      const params = new URLSearchParams({
        'sort[0][field]': sortField,
        'sort[0][dir]': sortDirection,
        skip: skip.toString(),
        pageSize: pageSize.toString()
      });

      const response = await Auth.fetch(
        `${Auth.ecms}/api/viewLocales?${params.toString()}`,
        { method: 'GET' }
      );

      if (response.status === 'SUCCESS') {
        return {
          data: response.locales,
          pageTotal: parseInt(response.pageTotal, 10)
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching locales:', error);
      return null;
    }
  }

  @action fetchLocaleNames = async () => {
    // Return cached data if available
    if (this.localesCache) {
      return this.localesCache;
    }

    try {
      const result = await this.fetchLocales('name', 'asc', 0, 100);
      if (result && result.data) {
        // Transform array to object: { id: name }
        const localeNames = {};
        result.data.forEach((locale) => {
          if (locale.id && locale.name) {
            localeNames[locale.id] = locale.name;
          }
        });
        // Cache the result
        this.localesCache = localeNames;
        return localeNames;
      }
      return null;
    } catch (error) {
      console.error('Error fetching locale names:', error);
      return null;
    }
  }

  @action clearLocalesCache = () => {
    this.localesCache = null;
  }

  // Publisher Options methods
  @observable publisherOptionsCache = null;

  @action fetchPublisherOptions = async () => {
    // Return cached data if available
    if (this.publisherOptionsCache) {
      return this.publisherOptionsCache;
    }

    try {
      const response = await Auth.fetch(
        `${Auth.ecms}/api/viewMyPublisherOptions`,
        { method: 'GET' }
      );

      if (response.status === 'SUCCESS') {
        const options = {
          publisherId: response.publisherId,
          publisherName: response.publisherName,
          hasDocReaderId: response.hasDocReaderId,
          hasWebReaderId: response.hasWebReaderId,
          hasTextHelpId: response.hasTextHelpId,
          hasTextHelpReactId: response.hasTextHelpReactId,
          hasWebSpellCheckerId: response.hasWebSpellCheckerId,
          hasOxfordDictionaryId: response.hasOxfordDictionaryId,
          aiEnabled: response.aiEnabled,
          docReaderId: response.docReaderId,
          webReaderId: response.webReaderId,
          textHelpId: response.textHelpId,
          textHelpReactId: response.textHelpReactId,
          webSpellCheckerId: response.webSpellCheckerId
        };
        
        // Cache the result
        this.publisherOptionsCache = options;
        return options;
      }

      return null;
    } catch (error) {
      console.error('Error fetching publisher options:', error);
      return null;
    }
  }

  @action clearPublisherOptionsCache = () => {
    this.publisherOptionsCache = null;
  }

  @action updateUserLocale = async (userId, localeId) => {
    try {
      const body = {};
      if (userId) {
        body.userId = userId;
      }
      if (localeId) {
        body.localeId = localeId;
      }

      const response = await Auth.fetch(
        `${Auth.ecms}/api/updateUserLocale`,
        {
          body,
          method: 'POST'
        }
      );

      if (response && response.status === 'SUCCESS') {
        return {
          success: true,
          localeId: response.localeId
        };
      }

      return {
        success: false,
        message: response.statusMessage || 'Failed to update locale'
      };
    } catch (error) {
      console.error('Error updating user locale:', error);
      return {
        success: false,
        message: error.message || 'Failed to update locale'
      };
    }
  }

  @action clearAccommodationsRoster = () => {
    this.currentAccommodationsRosterMap = new Map();
  }

  @action updateStudentOrClassroomAccommodationOption = async (userId, accommodationId, shouldRemove) => {
    try {
      let endpoint;
      if (shouldRemove) {
        endpoint = userId ? 'removeUserAccommodation' : 'removeUserAccommodationAll';
      } else {
        endpoint = userId ? 'addUserAccommodation' : 'addUserAccommodationAll';
      }
      const body = userId ? {
        accommodationId,
        userId,
        classroomId: classroomManager.currentClassroomId
      } : {
        accommodationId,
        classroomId: classroomManager.currentClassroomId
      };
      const apiUrl = `${Auth.ecms}/api/${endpoint}`;
      const response = await Auth.fetch(apiUrl, {
        body,
        method: 'POST'
      });
      if (response && response.status === 'SUCCESS') {
        if (userId) {
          this.updateAccommodationRosterItem(response.data);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  @action updateAccommodationRosterItem = async (rosterItem) => {
    const offAccommodations = rosterItem.accommodationIds.split(',');
    rosterItem.offAccommodations = offAccommodations;
    this.currentAccommodationsRosterMap.set(rosterItem.id, rosterItem);
  }

  @action setWordToolsTTSDependent = (wordToolsTTSDependent) => {
    this.wordToolsTTSDependent = wordToolsTTSDependent;
  }

  /**
   * Update the language the Spot Translation tool (ReadSpeaker/TextHelp) translates into
   * for a user (SPOT_TRANSLATION_LOCALE_ID); null = "Default or All". Distinct from
   * updateUserLocale, which sets the Content Translation (C2C Translate) language (LOCALE_ID).
   */
  @action updateUserSpotTranslationLocale = async (userId, spotTranslationLocaleId) => {
    try {
      const body = {};
      if (userId) {
        body.userId = userId;
      }
      if (spotTranslationLocaleId) {
        body.spotTranslationLocaleId = spotTranslationLocaleId;
      }

      const response = await Auth.fetch(
        `${Auth.ecms}/api/updateUserSpotTranslationLocale`,
        {
          body,
          method: 'POST'
        }
      );

      if (response && response.status === 'SUCCESS') {
        return {
          success: true,
          spotTranslationLocaleId: response.spotTranslationLocaleId
        };
      }

      return {
        success: false,
        message: response.statusMessage || 'Failed to update spot translation locale'
      };
    } catch (error) {
      console.error('Error updating user spot translation locale:', error);
      return {
        success: false,
        message: error.message || 'Failed to update spot translation locale'
      };
    }
  }

  @action setHasAccommodationLocaleSelector = (hasAccommodationLocaleSelector) => {
    this.hasAccommodationLocaleSelector = hasAccommodationLocaleSelector;
  }

  @computed get currentAccommodationsRosterArray() {
    return Array.from(this.currentAccommodationsRosterMap?.values() || []);
  }

  @computed get togglerSectionOffAccommodations() {
    return Array.from(this.togglerSectionOffAccommodationsSet.values());
  }
}

export default new AccommodationsManager();
